import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { RecipeGuideIngredientType } from '@/src/types/apps/recipeGuideTypes'

export interface RecipeGuideIngredient {
  id: number
  name: string
  description: string
  externalCode: string
  imageUrl: string
  isActive: boolean
  country: string
  type: RecipeGuideIngredientType
  createdAt: string
  updatedAt: string
}

export interface RecipeGuideIngredientResponse {
  data: RecipeGuideIngredient
}

export interface RecipeGuideIngredientsResponse {
  data: RecipeGuideIngredient[]
  meta: {
    total: number
    pages: number
  }
}

export interface RecipeGuideIngredientStats {
  totalIngredients: number
  ingredientsWithImage: number
  activeIngredients: number
  inactiveIngredients: number
}

interface RecipeGuideIngredientQueryOptions {
  take?: number
  skip?: number
  sortBy?: string
  filters?: Record<string, any>
}

interface RecipeGuideIngredientInitialState {
  ingredients: RecipeGuideIngredient[]
  ingredient: RecipeGuideIngredient | null
  stats: RecipeGuideIngredientStats
  loading: boolean
  meta: RecipeGuideIngredientsResponse['meta']
  error: any
  success: string | null
}

interface RecipeGuideIngredientClonePayload {
  sourceRecipeIngredientId: number
  name: string
  country: string
  isActive?: boolean
}

const initialState: RecipeGuideIngredientInitialState = {
  ingredients: [],
  ingredient: null,
  stats: {
    totalIngredients: 0,
    ingredientsWithImage: 0,
    activeIngredients: 0,
    inactiveIngredients: 0
  },
  loading: false,
  meta: {
    total: 0,
    pages: 0
  },
  error: null,
  success: null
}

export const getRecipeGuideIngredients = createAsyncThunk<
  RecipeGuideIngredientsResponse,
  RecipeGuideIngredientQueryOptions,
  { rejectValue: any; extra: { httpClient: any } }
>('gygRecipeGuideIngredients/getRecipeGuideIngredients', async (queryOptions, { rejectWithValue, extra }) => {
  try {
    const response = await extra.httpClient.get(`recipe-guide-ingredients`, {
      params: queryOptions
    })

    return response.data
  } catch (error: any) {
    return rejectWithValue(error.response.data)
  }
})

export const createRecipeGuideIngredient = createAsyncThunk<
  RecipeGuideIngredientResponse,
  FormData,
  { rejectValue: any; extra: { httpClient: any } }
>('gygRecipeGuideIngredients/createRecipeGuideIngredient', async (formData, { rejectWithValue, extra }) => {
  try {
    const response = await extra.httpClient.post('recipe-guide-ingredients', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })

    return response.data
  } catch (error: any) {
    return rejectWithValue(error.response.data)
  }
})

export const cloneRecipeGuideIngredient = createAsyncThunk<
  RecipeGuideIngredientResponse,
  RecipeGuideIngredientClonePayload,
  { rejectValue: any; extra: { httpClient: any } }
>('gygRecipeGuideIngredients/cloneRecipeGuideIngredient', async (payload, { rejectWithValue, extra }) => {
  try {
    const response = await extra.httpClient.post('recipe-guide-ingredients/clone', payload)

    return response.data
  } catch (error: any) {
    return rejectWithValue(error.response.data)
  }
})

export const updateRecipeGuideIngredient = createAsyncThunk<
  RecipeGuideIngredientResponse,
  { id: number; data: FormData },
  { rejectValue: any; extra: { httpClient: any } }
>('gygRecipeGuideIngredients/updateRecipeGuideIngredient', async ({ id, data }, { rejectWithValue, extra }) => {
  try {
    const response = await extra.httpClient.put(`recipe-guide-ingredients/${id}`, data, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })

    return response.data
  } catch (error: any) {
    return rejectWithValue(error.response.data)
  }
})

export const getRecipeGuideIngredient = createAsyncThunk<
  RecipeGuideIngredientResponse,
  number,
  { rejectValue: any; extra: { httpClient: any } }
>('gygRecipeGuideIngredients/getRecipeGuideIngredient', async (id, { rejectWithValue, extra }) => {
  try {
    const response = await extra.httpClient.get(`recipe-guide-ingredients/${id}`)

    return response.data
  } catch (error: any) {
    return rejectWithValue(error.response.data)
  }
})

export const deleteRecipeGuideIngredient = createAsyncThunk<
  { id: number },
  number,
  { rejectValue: any; extra: { httpClient: any } }
>('gygRecipeGuideIngredients/deleteRecipeGuideIngredient', async (id, { rejectWithValue, extra }) => {
  try {
    await extra.httpClient.delete(`recipe-guide-ingredients/${id}`)

    return { id }
  } catch (error: any) {
    return rejectWithValue(error.response.data)
  }
})

export const getRecipeGuideIngredientStats = createAsyncThunk<
  { data: RecipeGuideIngredientStats },
  { country: string },
  { rejectValue: any; extra: { httpClient: any } }
>('gygRecipeGuideIngredients/getRecipeGuideIngredientStats', async ({ country }, { rejectWithValue, extra }) => {
  try {
    const response = await extra.httpClient.get('recipe-guide-ingredients/stats', {
      params: { country }
    })

    return response.data
  } catch (error: any) {
    return rejectWithValue(error.response.data)
  }
})

const gygRecipeGuideIngredientSlice = createSlice({
  name: 'gygRecipeGuideIngredients',
  initialState,
  reducers: {
    resetSuccessAndError: state => {
      state.success = null
      state.error = null
    }
  },
  extraReducers: builder => {
    builder.addCase(getRecipeGuideIngredients.pending, state => {
      state.loading = true
      state.error = null
    })
    builder.addCase(getRecipeGuideIngredients.fulfilled, (state, action) => {
      state.loading = false
      state.error = null
      state.success = null
      state.ingredients = action.payload.data
      state.meta = action.payload.meta
    })
    builder.addCase(getRecipeGuideIngredients.rejected, (state, action) => {
      state.loading = false
      state.error = action.payload || 'Failed to fetch recipe guide ingredients'
    })

    builder.addCase(createRecipeGuideIngredient.pending, state => {
      state.loading = true
      state.error = null
      state.success = null
    })
    builder.addCase(createRecipeGuideIngredient.fulfilled, state => {
      state.loading = false
      state.meta.total += 1
      state.success = 'Recipe guide ingredient created successfully'
    })
    builder.addCase(createRecipeGuideIngredient.rejected, (state, action) => {
      state.loading = false
      state.error = action.payload || 'Failed to create recipe guide ingredient'
    })

    // Clone ingredient
    builder.addCase(cloneRecipeGuideIngredient.pending, state => {
      state.loading = true
      state.error = null
      state.success = null
    })
    builder.addCase(cloneRecipeGuideIngredient.fulfilled, (state, action) => {
      state.loading = false
      state.ingredients.unshift(action.payload.data)
      state.meta.total += 1
      state.success = 'Recipe guide ingredient cloned successfully'
    })
    builder.addCase(cloneRecipeGuideIngredient.rejected, (state, action) => {
      state.loading = false
      state.error = action.payload || 'Failed to clone recipe guide ingredient'
    })

    builder.addCase(updateRecipeGuideIngredient.pending, state => {
      state.loading = true
      state.error = null
      state.success = null
    })
    builder.addCase(updateRecipeGuideIngredient.fulfilled, state => {
      state.loading = false
      state.success = 'Recipe guide ingredient updated successfully'
    })
    builder.addCase(updateRecipeGuideIngredient.rejected, (state, action) => {
      state.loading = false
      state.error = action.payload || 'Failed to update recipe guide ingredient'
    })

    builder.addCase(getRecipeGuideIngredient.pending, state => {
      state.loading = true
      state.error = null
      state.success = null
    })
    builder.addCase(getRecipeGuideIngredient.fulfilled, (state, action) => {
      state.loading = false
      state.ingredient = action.payload.data
    })
    builder.addCase(getRecipeGuideIngredient.rejected, (state, action) => {
      state.loading = false
      state.error = action.payload || 'Failed to fetch recipe guide ingredient'
    })

    builder.addCase(deleteRecipeGuideIngredient.pending, state => {
      state.loading = true
      state.error = null
      state.success = null
    })
    builder.addCase(deleteRecipeGuideIngredient.fulfilled, (state, action) => {
      state.loading = false
      state.ingredients = state.ingredients.filter(e => e.id !== action.payload.id)
      state.meta.total -= 1
      state.success = 'Recipe guide ingredient deleted successfully'
    })
    builder.addCase(deleteRecipeGuideIngredient.rejected, (state, action) => {
      state.loading = false
      state.error = action.payload || 'Failed to delete recipe guide ingredient'
    })

    builder.addCase(getRecipeGuideIngredientStats.pending, state => {
      state.loading = true
      state.error = null
    })
    builder.addCase(getRecipeGuideIngredientStats.fulfilled, (state, action) => {
      state.loading = false
      state.stats = action.payload.data
    })
    builder.addCase(getRecipeGuideIngredientStats.rejected, (state, action) => {
      state.loading = false
      state.error = action.payload || 'Failed to fetch recipe guide ingredient stats'
    })
  }
})

export const { resetSuccessAndError } = gygRecipeGuideIngredientSlice.actions
export default gygRecipeGuideIngredientSlice.reducer
